import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Shield, Loader2, Save, Building2, Lock, Clock, MapPin, Users } from "lucide-react";

export default function CondoRules() {
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});

  useEffect(() => {
    api.get("/condo/rules").then(r => { setVenues(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const updateRules = (venueId, field, value) => {
    setVenues(vs => vs.map(v =>
      v.venue_id === venueId ? { ...v, rules: { ...v.rules, [field]: value } } : v
    ));
  };

  const saveRules = async (venueId) => {
    const venue = venues.find(v => v.venue_id === venueId);
    if (!venue) return;
    setSaving(s => ({ ...s, [venueId]: true }));
    try {
      await api.put(`/condo/rules/${venueId}`, venue.rules);
      toast.success(`Rules updated for ${venue.name}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save rules"); }
    setSaving(s => ({ ...s, [venueId]: false }));
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-court-orange" /></div>;

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-6 md:py-10" data-testid="condo-rules-page">
      <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight flex items-center gap-3 mb-1">
        <Shield className="w-8 h-8 text-court-orange" />Facility Rules
      </h1>
      <p className="text-muted-foreground mb-8">Configure access rules for each venue</p>

      {venues.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed border-slate-200">
          <Building2 className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="font-heading text-lg text-muted-foreground uppercase">No venues configured</p>
        </div>
      ) : (
        <div className="space-y-6">
          {venues.map(v => (
            <div key={v.venue_id} className="bg-white border-2 border-slate-100 p-6" data-testid={`rules-venue-${v.venue_id}`}>
              <div className="flex items-center gap-3 mb-5">
                <Building2 className="w-5 h-5 text-court-orange" />
                <div>
                  <h3 className="font-heading text-lg font-bold uppercase">{v.name}</h3>
                  <p className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="w-3 h-3" />{v.city} - {v.resources_count} court{v.resources_count !== 1 ? 's' : ''}</p>
                </div>
              </div>

              <div className="space-y-5">
                {/* Resident Only Toggle */}
                <div className="flex items-center justify-between p-4 bg-slate-50 border border-slate-200">
                  <div className="flex items-center gap-3">
                    <Lock className={`w-5 h-5 ${v.rules?.resident_only ? 'text-court-orange' : 'text-muted-foreground'}`} />
                    <div>
                      <p className="font-medium text-sm">Resident-Only Access</p>
                      <p className="text-xs text-muted-foreground">Only verified residents can book this facility</p>
                    </div>
                  </div>
                  <Switch data-testid={`resident-only-${v.venue_id}`} checked={v.rules?.resident_only || false}
                    onCheckedChange={val => updateRules(v.venue_id, "resident_only", val)} />
                </div>

                {/* Max Bookings */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs font-heading uppercase tracking-wider flex items-center gap-1">
                      <Users className="w-3 h-3" />Max Bookings / Day
                    </Label>
                    <Input data-testid={`max-bookings-${v.venue_id}`} type="number" min="1" max="10"
                      value={v.rules?.max_bookings_per_day || 2}
                      onChange={e => updateRules(v.venue_id, "max_bookings_per_day", parseInt(e.target.value) || 2)}
                      className="h-11 rounded-none border-2 mt-1" />
                    <p className="text-[10px] text-muted-foreground mt-1">Per resident per day</p>
                  </div>
                  <div>
                    <Label className="text-xs font-heading uppercase tracking-wider flex items-center gap-1">
                      <Clock className="w-3 h-3" />Open From
                    </Label>
                    <Input data-testid={`hours-start-${v.venue_id}`} type="time"
                      value={v.rules?.allowed_hours_start || "06:00"}
                      onChange={e => updateRules(v.venue_id, "allowed_hours_start", e.target.value)}
                      className="h-11 rounded-none border-2 mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs font-heading uppercase tracking-wider flex items-center gap-1">
                      <Clock className="w-3 h-3" />Open Until
                    </Label>
                    <Input data-testid={`hours-end-${v.venue_id}`} type="time"
                      value={v.rules?.allowed_hours_end || "22:00"}
                      onChange={e => updateRules(v.venue_id, "allowed_hours_end", e.target.value)}
                      className="h-11 rounded-none border-2 mt-1" />
                  </div>
                </div>

                <Button data-testid={`save-rules-${v.venue_id}`} onClick={() => saveRules(v.venue_id)}
                  disabled={saving[v.venue_id]}
                  className="bg-court-orange hover:bg-court-orange-hover text-white rounded-full font-heading uppercase px-6 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]">
                  {saving[v.venue_id] ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Rules
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
